class ItemsScroll extends HTMLElement {
  items;
  scrollBar;
  scrollBarTrack;
  navigationContainer;
  navigationPrevButton;
  navigationNextButton;
  pageNavigationContainer;
  pageNavigationWrapper;
  pageNavigationCounter;

  #ticking;
  #isScrollBarDragged;
  #boundItemsScroll;
  #boundPageNavigationClick;
  #itemsPerPage;
  #navigationPagesAmount;
  #itemAtScrollIndex;
  #controlsListenersEnabled;
  #boundPrevNavigationAction;
  #boundNextNavigationAction;
  #isNavigating;
  #isTouching;
  #idleTimer;
  #itemSpanCache;
  #lastPageIndex;

  constructor() {
    super();

    this.items = this.querySelector('[data-items]');
    this.pageNavigationContainer = null;
    this.breakpoints = {
      mob: 480,
      phab: 560,
      tab: 768,
      desk: 980
    };
    this.breakpointMin = Object.keys(this.breakpoints).includes(this.getAttribute('breakpoint-min'))
      ? this.breakpoints[this.getAttribute('breakpoint-min')]
      : Number(this.getAttribute('breakpoint-min') || '0');
    this.breakpointMax = Object.keys(this.breakpoints).includes(this.getAttribute('breakpoint-max'))
      ? this.breakpoints[this.getAttribute('breakpoint-max')]
      : Number(this.getAttribute('breakpoint-max') || 'Infinity');

    this.pageNavigationWrapper = null;
    this.navigationContainer = null;
    this.navigationPrevButton = null;
    this.navigationNextButton = null;

    this.navigationCounterOnly = this.hasAttribute('mobile-counter-only');
    this.navigationCounterSeparator = '/';
    this.navigationCounterShowForPagesAmount = 7;
    this.navigationCounterShowUpToContainerSize = 768;

    this.infiniteLoop = this.getAttribute('infinite-loop') === 'true';

    this.originalItems = [];
    this.cloneItems = [];
    this.originalItemsCount = 0;
    this.originalTrackWidth = 0;
    this.firstCloneOffset = 0;

    // Exact scroll distance between an original item and its clone.
    // Measured from offsetLeft so it INCLUDES flex column-gap — summing
    // item widths does not, and jumping by that shorter distance lands
    // the track off its snap points (visible as an off-center slide).
    this.loopJump = 0;

    this.#ticking = false;
    this.#isScrollBarDragged = false;

    this.#boundItemsScroll = null;
    this.#boundPageNavigationClick = null;
    this.#boundPrevNavigationAction = null;
    this.#boundNextNavigationAction = null;

    this.#isNavigating = false;
    this.#isTouching = false;
    this.#idleTimer = 0;
    this.#itemSpanCache = 0;
    this.#lastPageIndex = -1;
    this.#controlsListenersEnabled = false;

    // scrollend fires once momentum AND snap settling are fully done —
    // the only truly safe moment to normalize the loop position.
    this.supportsScrollEnd = 'onscrollend' in window;
  }

  connectedCallback() {
    if (!this.items) throw Error('Items container missing');

    const debounce = (f, delay) => {
      let timer = 0;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => f.apply(this, args), delay);
      };
    };

    this.#refreshLoopMetrics();

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        this.#updateControls();
      }, 50)
    );

    resizeObserver.observe(this.items);

    const options = {
      root: this.items,
      rootMargin: '0px',
      threshold: 1
    };

    const intersectionObserver = new IntersectionObserver((entries) => {
      if (this.navigationContainer && !this.infiniteLoop) {
        entries.forEach(({ target, isIntersecting }) => {
          if (!target.previousElementSibling) this.navigationPrevButton.disabled = isIntersecting;
          if (!target.nextElementSibling) this.navigationNextButton.disabled = isIntersecting;
        });
      }
    }, options);

    if (this.items.firstElementChild) intersectionObserver.observe(this.items.firstElementChild);
    if (this.items.lastElementChild) intersectionObserver.observe(this.items.lastElementChild);

    if (this.infiniteLoop) {
      // Never normalize the scroll position while a finger is on the
      // track or momentum may still be running — an instant scrollTo
      // mid-gesture makes iOS snap scrolling reverse direction (the
      // "boomerang"). We only jump once scrolling has gone idle.
      this.items.addEventListener('touchstart', () => {
        this.#isTouching = true;
        clearTimeout(this.#idleTimer);
      }, { passive: true });

      const endTouch = () => {
        this.#isTouching = false;
        this.#scheduleIdleNormalize();
      };

      this.items.addEventListener('touchend', endTouch, { passive: true });
      this.items.addEventListener('touchcancel', endTouch, { passive: true });

      if (this.supportsScrollEnd) {
        this.items.addEventListener('scrollend', () => {
          if (this.#isTouching || this.#isNavigating) return;
          this.#normalizeInfiniteScrollPosition(false);
          this.#setPageNavigationState();
        });
      }
    }

    this.#updateControls();
  }

  #refreshLoopMetrics() {
    const allChildren = Array.from(this.items.children);

    if (!this.infiniteLoop) {
      this.originalItems = allChildren;
      this.cloneItems = [];
      this.originalItemsCount = allChildren.length;
      this.originalTrackWidth = this.#getItemsWidth(allChildren);
      this.firstCloneOffset = 0;
      this.loopJump = 0;
      return;
    }

    const firstCloneIndex = allChildren.findIndex((item) => item.classList.contains('is-loop-clone'));

    if (firstCloneIndex === -1) {
      this.originalItems = allChildren;
      this.cloneItems = [];
      this.originalItemsCount = allChildren.length;
      this.originalTrackWidth = this.#getItemsWidth(allChildren);
      this.firstCloneOffset = 0;
      this.loopJump = 0;
      return;
    }

    this.originalItems = allChildren.slice(0, firstCloneIndex);
    this.cloneItems = allChildren.slice(firstCloneIndex);
    this.originalItemsCount = this.originalItems.length;
    this.originalTrackWidth = this.#getItemsWidth(this.originalItems);
    this.firstCloneOffset = this.cloneItems.length ? this.cloneItems[0].offsetLeft : this.originalTrackWidth;

    if (this.cloneItems.length && this.originalItems.length) {
      // getBoundingClientRect is subpixel-precise; offsetLeft rounds to
      // whole pixels. Slide widths on phones are fractional, so a rounded
      // jump lands the track slightly off its snap point and the browser
      // visibly re-snaps — that reads as a stutter at the wrap seam.
      this.loopJump =
        this.cloneItems[0].getBoundingClientRect().left -
        this.originalItems[0].getBoundingClientRect().left;
      if (this.loopJump <= 0) this.loopJump = 0; // clones hidden or not laid out
    } else {
      this.loopJump = 0;
    }
  }

  #getItemsWidth(items) {
    if (!items.length) return 0;

    return items.reduce((total, item) => {
      const style = window.getComputedStyle(item);
      const marginLeft = Number(style.getPropertyValue('margin-left').replace('px', '')) || 0;
      const marginRight = Number(style.getPropertyValue('margin-right').replace('px', '')) || 0;
      return total + item.getBoundingClientRect().width + marginLeft + marginRight;
    }, 0);
  }

  #normalizeIndex(index) {
    if (!this.originalItemsCount) return 0;
    return ((index % this.originalItemsCount) + this.originalItemsCount) % this.originalItemsCount;
  }

  #getEffectiveChildren() {
    return this.originalItemsCount ? this.originalItems : Array.from(this.items.children);
  }

  // Computed styles + rects force a synchronous layout. This runs on every
  // scroll frame via #setPageNavigationState, so the span is cached and only
  // invalidated on resize (#updateControls) — repeated forced layouts during
  // touch scrolling cause visible jank on phones.
  #getItemSpan() {
    if (this.#itemSpanCache > 0) return this.#itemSpanCache;

    const firstItem = this.items.firstElementChild;
    if (!firstItem) return 0;

    const itemsComputedStyle = window.getComputedStyle(this.items);
    const itemsGap = Number(itemsComputedStyle.getPropertyValue('column-gap').replace('px', '')) || 0;

    const itemComputedStyle = window.getComputedStyle(firstItem);
    const itemMarginLeft = Number(itemComputedStyle.getPropertyValue('margin-left').replace('px', '')) || 0;
    const itemMarginRight = Number(itemComputedStyle.getPropertyValue('margin-right').replace('px', '')) || 0;

    this.#itemSpanCache =
      itemMarginLeft +
      firstItem.getBoundingClientRect().width +
      itemMarginRight +
      itemsGap;

    return this.#itemSpanCache;
  }

  #getItemAtScroll() {
    const children = Array.from(this.items.children);
    if (!children.length) return null;

    const itemSpan = this.#getItemSpan();

    const roughIndex = itemSpan > 0 ? Math.round(this.items.scrollLeft / itemSpan) : 0;
    const safeIndex = Math.max(0, Math.min(roughIndex, children.length - 1));

    return children[safeIndex];
  }

  // Instant scroll jump that cannot be seen and cannot trigger a re-snap:
  // snapping is disabled for the jump and restored after a forced reflow.
  // Deltas are exact multiples of loopJump, so a snapped position stays
  // snapped after the jump.
  #instantJumpBy(delta) {
    if (!delta) return;

    const previousSnap = this.items.style.scrollSnapType;
    this.items.style.scrollSnapType = 'none';

    this.items.scrollTo({
      left: this.items.scrollLeft + delta,
      behavior: 'instant'
    });

    void this.items.offsetHeight; // flush layout before snapping is restored
    this.items.style.scrollSnapType = previousSnap;
  }

  #scheduleIdleNormalize() {
    clearTimeout(this.#idleTimer);
    this.#idleTimer = setTimeout(() => {
      if (this.#isTouching || this.#isNavigating) return;
      this.#normalizeInfiniteScrollPosition(false);
      this.#setPageNavigationState();
    }, 300);
  }

  #handleItemsScroll() {
    if (!this.#ticking) {
      window.requestAnimationFrame(() => {
        this.#setPageNavigationState();
        this.#ticking = false;
      });

      this.#ticking = true;
    }

    // Loop housekeeping is deferred until scrolling goes idle — see the
    // note in connectedCallback. Where scrollend is supported it is the
    // trigger (it fires strictly after momentum AND snap settling); the
    // timer is only a fallback for older browsers, where a long-enough
    // quiet window is the best available signal.
    if (this.infiniteLoop && !this.#isNavigating && !this.supportsScrollEnd) {
      this.#scheduleIdleNormalize();
    }
  }

  #normalizeInfiniteScrollPosition(force = false) {
    if (!this.infiniteLoop || !this.loopJump || !this.cloneItems.length) return;

    const currentLeft = this.items.scrollLeft;
    const threshold = this.firstCloneOffset || this.loopJump;

    if (force || currentLeft >= threshold - 1) {
      this.#instantJumpBy(-this.loopJump);
    }
  }

  #jumpToEquivalentOriginalFromClone() {
    if (!this.infiniteLoop || !this.cloneItems.length || !this.loopJump) return;

    const currentItem = this.#getItemAtScroll();
    if (!currentItem || !currentItem.classList.contains('is-loop-clone')) return;

    this.#instantJumpBy(-this.loopJump);
  }

  #updatePageNavigationList() {
    if (!this.pageNavigationWrapper) {
      this.pageNavigationWrapper = document.createElement('div');
      this.pageNavigationWrapper.classList.add('page-navigation-wrapper');

      this.appendChild(this.pageNavigationWrapper);
    }

    const effectiveChildren = this.#getEffectiveChildren();
    if (!effectiveChildren.length) return;

    const firstItemWidth = effectiveChildren[0].offsetWidth || 1;
    const itemsPerPage = Math.max(1, Math.floor(this.items.offsetWidth / firstItemWidth));
    const pagesAmount = Math.ceil(effectiveChildren.length / itemsPerPage);

    if (pagesAmount !== this.#navigationPagesAmount || itemsPerPage !== this.#itemsPerPage) {
      this.#itemsPerPage = itemsPerPage;
      this.#navigationPagesAmount = pagesAmount;
      this.#lastPageIndex = -1; // navigation rebuilt — force next state write

      if (
        this.navigationCounterShowUpToContainerSize &&
        this.navigationCounterShowUpToContainerSize >= this.offsetWidth &&
        this.navigationCounterShowForPagesAmount &&
        (this.navigationCounterOnly || this.#navigationPagesAmount >= this.navigationCounterShowForPagesAmount)
      ) {
        if (this.pageNavigationContainer) this.pageNavigationContainer.setAttribute('hidden', true);

        if (!this.pageNavigationCounter) {
          this.pageNavigationCounter = document.createElement('div');
          this.pageNavigationCounter.classList.add('page-navigation-counter');

          this.pageNavigationWrapper.appendChild(this.pageNavigationCounter);
        }

        this.pageNavigationCounter.removeAttribute('hidden');

        this.pageNavigationCounter.innerHTML = `
          <span class="page-navigation-counter__current">${(this.#itemAtScrollIndex || 0) + 1}</span>
          <span class="page-navigation-counter__separator">${this.navigationCounterSeparator}</span>
          <span class="page-navigation-counter__total">${this.#navigationPagesAmount}</span>
        `;

        this.#setPageNavigationState();
      } else {
        if (this.pageNavigationCounter) this.pageNavigationCounter.setAttribute('hidden', true);

        if (!this.pageNavigationContainer) {
          this.pageNavigationContainer = document.createElement('ul');
          this.pageNavigationContainer.classList.add('page-navigation');

          this.pageNavigationWrapper.appendChild(this.pageNavigationContainer);
        }

        this.pageNavigationContainer.removeAttribute('hidden');

        if (this.#navigationPagesAmount > 1) {
          this.pageNavigationContainer.innerHTML = `
            ${Array.from({ length: pagesAmount }, (_, i) => `
              <li class="page-navigation__item" role="presentation">
                <button type="button" role="tab" class="page-navigation__button" data-page="${i + 1}" aria-label="Scroll to page ${i + 1} of ${this.#navigationPagesAmount}" tabindex="-1">
                  <span class="visually-hidden">${i + 1}</span>
                </button>
              </li>
            `).join('\n')}
          `;

          this.#setPageNavigationState();
        } else {
          this.pageNavigationContainer.innerHTML = '';
        }
      }
    }
  }

  #handlePageNavigationClick(e) {
    const { target } = e;
    if (!target.classList.contains('page-navigation__button')) return;

    const page = Number(target.dataset.page);
    const effectiveChildren = this.#getEffectiveChildren();
    const targetItem = effectiveChildren[this.#itemsPerPage * (page - 1)];

    if (!targetItem) return;

    this.#moveItems(targetItem.offsetLeft, 'smooth');
  }

  #createNavigation() {
  const navigationContainer = document.createElement('div');

  navigationContainer.classList.add('navigation');
  navigationContainer.setAttribute('role', 'group');
  navigationContainer.setAttribute('aria-label', 'Carousel navigation');

  navigationContainer.innerHTML = `
    <button type="button" class="navigation__button navigation__button--prev" data-navigation-prev aria-label="Scroll left"><i class="icon icon--left" aria-hidden="true"></i></button>
    <button type="button" class="navigation__button navigation__button--next" data-navigation-next aria-label="Scroll right"><i class="icon icon--right" aria-hidden="true"></i></button>
  `;

  this.appendChild(navigationContainer);

  this.navigationContainer = navigationContainer;
  this.navigationPrevButton = navigationContainer.querySelector('[data-navigation-prev]');
  this.navigationNextButton = navigationContainer.querySelector('[data-navigation-next]');
}

  #handlePrevNavigationAction(e) {
    e.preventDefault();
    if (this.#isNavigating) return;

    const itemAtScroll = this.#getItemAtScroll();
    if (!itemAtScroll) return;

    let previousItem = itemAtScroll.previousElementSibling;

    if (this.infiniteLoop && !previousItem && this.loopJump) {
      this.#instantJumpBy(this.loopJump);
      previousItem = this.items.children[this.items.children.length - this.cloneItems.length - 1] || null;
    }

    if (previousItem) {
      this.#isNavigating = true;
      this.classList.add('is-scrolling');

      this.smoothScrollItems(previousItem.offsetLeft)
        .then(() => {
          if (this.infiniteLoop) {
            this.#jumpToEquivalentOriginalFromClone();
            this.#normalizeInfiniteScrollPosition(false);
          }
        })
        .finally(() => {
          this.#isNavigating = false;
          this.classList.remove('is-scrolling');
        });
    }
  }

  #handleNextNavigationAction(e) {
    e.preventDefault();
    if (this.#isNavigating) return;

    const itemAtScroll = this.#getItemAtScroll();
    if (!itemAtScroll) return;

    let nextItem = itemAtScroll.nextElementSibling;

    if (this.infiniteLoop && !nextItem && this.cloneItems.length) {
      nextItem = this.cloneItems[0];
    }

    if (nextItem) {
      this.#isNavigating = true;
      this.classList.add('is-scrolling');

      this.smoothScrollItems(nextItem.offsetLeft)
        .then(() => {
          if (this.infiniteLoop) {
            this.#jumpToEquivalentOriginalFromClone();
            this.#normalizeInfiniteScrollPosition(false);
          }
        })
        .finally(() => {
          this.#isNavigating = false;
          this.classList.remove('is-scrolling');
        });
    }
  }

  #enableControlsListeners() {
    if (this.#controlsListenersEnabled) return;

    this.#boundItemsScroll = this.#handleItemsScroll.bind(this);
    this.#boundPageNavigationClick = this.#handlePageNavigationClick.bind(this);
    this.#boundPrevNavigationAction = this.#handlePrevNavigationAction.bind(this);
    this.#boundNextNavigationAction = this.#handleNextNavigationAction.bind(this);

    this.items.addEventListener('scroll', this.#boundItemsScroll);
    if (this.pageNavigationContainer) this.pageNavigationContainer.addEventListener('click', this.#boundPageNavigationClick);
    this.navigationPrevButton.addEventListener('click', this.#boundPrevNavigationAction);
    this.navigationNextButton.addEventListener('click', this.#boundNextNavigationAction);

    this.#controlsListenersEnabled = true;
  }

  #disableControlsListeners() {
    if (!this.#controlsListenersEnabled) return;

    this.items.removeEventListener('scroll', this.#boundItemsScroll);
    if (this.pageNavigationContainer) this.pageNavigationContainer.removeEventListener('click', this.#boundPageNavigationClick);
    this.navigationPrevButton.removeEventListener('click', this.#boundPrevNavigationAction);
    this.navigationNextButton.removeEventListener('click', this.#boundNextNavigationAction);

    this.#controlsListenersEnabled = false;
  }

  #updateControls() {
    this.#itemSpanCache = 0; // layout may have changed — remeasure lazily
    this.#refreshLoopMetrics();

    if (
      document.body.offsetWidth > this.breakpointMin &&
      document.body.offsetWidth <= this.breakpointMax
    ) {
      this.setAttribute('scroll-enabled', '');
    } else {
      this.removeAttribute('scroll-enabled');
    }

    // Decide scrollability from the ORIGINAL items only. Loop clones
    // always extend the track, so measuring scrollWidth with clones
    // visible would force every looped carousel to scroll even when
    // all of its real slides already fit (e.g. 5 tiles in a 5-across
    // grid on desktop). Hide clones, measure, then only bring them
    // back if the real slides overflow.
    let canScroll;

    if (this.infiniteLoop && this.cloneItems.length) {
      this.cloneItems.forEach((clone) => { clone.style.display = 'none'; });
      canScroll = this.items.scrollWidth > this.items.clientWidth + 1;

      if (canScroll && this.hasAttribute('scroll-enabled')) {
        this.cloneItems.forEach((clone) => { clone.style.display = ''; });
      }

      this.#refreshLoopMetrics();
    } else {
      canScroll = this.items.scrollWidth > this.items.offsetWidth;
    }

    if (this.hasAttribute('scroll-enabled') && canScroll) {
      this.setAttribute('scrollable', '');

      if (!this.navigationContainer) this.#createNavigation();

      this.#updatePageNavigationList();

      const effectiveChildren = this.#getEffectiveChildren();
      if (effectiveChildren[0] && effectiveChildren[0].offsetWidth > this.items.offsetWidth / 2) {
        this.setAttribute('snap', 'center');
      } else {
        this.setAttribute('snap', 'start');
      }

      if (this.infiniteLoop && this.navigationPrevButton && this.navigationNextButton) {
        this.navigationPrevButton.disabled = false;
        this.navigationNextButton.disabled = false;
      }

      this.#enableControlsListeners();
    } else {
      if (this.navigationContainer) {
        this.#disableControlsListeners();
      }

      this.removeAttribute('scrollable');
    }
  }

  #setPageNavigationState() {
    if (!this.#navigationPagesAmount || this.#navigationPagesAmount <= 1) return;

    const itemAtScroll = this.#getItemAtScroll();
    if (!itemAtScroll) return;

    let itemAtScrollIndex = [...this.items.children].indexOf(itemAtScroll);
    if (this.infiniteLoop) {
      itemAtScrollIndex = this.#normalizeIndex(itemAtScrollIndex);
    }

    this.#itemAtScrollIndex = itemAtScrollIndex;

    const effectiveLength = this.originalItemsCount || this.items.children.length;

    const currentNavigationPageIndex =
      this.#itemAtScrollIndex + this.#itemsPerPage > effectiveLength - 1
        ? this.#navigationPagesAmount - 1
        : Math.floor(this.#itemAtScrollIndex / this.#itemsPerPage);

    // Skip DOM writes when nothing changed — this runs on every scroll
    // frame and unconditional writes force style recalc mid-scroll.
    if (currentNavigationPageIndex === this.#lastPageIndex) return;
    this.#lastPageIndex = currentNavigationPageIndex;

    if (this.pageNavigationContainer) {
      const active = this.pageNavigationContainer.querySelector('.is-active');
      if (active) active.classList.remove('is-active');

      if (this.pageNavigationContainer.children[currentNavigationPageIndex]) {
        this.pageNavigationContainer.children[currentNavigationPageIndex]
          .querySelector('button')
          .classList.add('is-active');
      }
    }

    if (this.pageNavigationCounter) {
      const pageNavigationCounterCurrentElement = this.pageNavigationCounter.querySelector('.page-navigation-counter__current');

      this.pageNavigationCounter.setAttribute(
        'aria-label',
        `Page ${currentNavigationPageIndex + 1} of ${this.#navigationPagesAmount}`
      );

      if (pageNavigationCounterCurrentElement) {
        pageNavigationCounterCurrentElement.innerHTML = currentNavigationPageIndex + 1;
      }
    }
  }

  #moveItems(position, behavior = 'instant') {
    let itemsScrollX = position;

    if (!this.infiniteLoop) {
      if (itemsScrollX < 0) {
        itemsScrollX = 0;
      } else if (itemsScrollX > this.items.scrollWidth - this.items.offsetWidth) {
        itemsScrollX = this.items.scrollWidth - this.items.offsetWidth;
      }
    } else {
      if (itemsScrollX < 0) itemsScrollX = 0;
    }

    if (behavior === 'instant') {
      this.items.scrollTo({
        behavior: 'instant',
        left: itemsScrollX
      });

      if (this.infiniteLoop) {
        this.#jumpToEquivalentOriginalFromClone();
        this.#normalizeInfiniteScrollPosition(false);
      }
    } else {
      this.#isNavigating = true;
      this.classList.add('is-scrolling');

      this.smoothScrollItems(itemsScrollX)
        .then(() => {
          if (this.infiniteLoop) {
            this.#jumpToEquivalentOriginalFromClone();
            this.#normalizeInfiniteScrollPosition(false);
          }
        })
        .finally(() => {
          this.#isNavigating = false;
          this.classList.remove('is-scrolling');
        });
    }
  }

  smoothScrollItems(position) {
    return new Promise((resolve, reject) => {
      let same = 0;
      let lastPos = null;

      const check = () => {
        const newPos = this.items.scrollLeft;

        if (this.#isScrollBarDragged) return reject(newPos);

        if (newPos === lastPos) {
          if (same++ > 2) {
            return resolve(position);
          }
        } else {
          same = 0;
          lastPos = newPos;
        }

        window.requestAnimationFrame(check);
      };

      this.items.scrollTo({
        left: position,
        behavior: 'smooth'
      });

      window.requestAnimationFrame(check);
    });
  }
}

if (!customElements.get('items-scroll')) {
  customElements.define('items-scroll', ItemsScroll);
}
